/*
Functions to use for random error checks that are fairly common to different functions
e.g. initial fetch then to work out whether to when to jsonify a return or not
 */

// Longest message we'll hand to a toast - a payload with several failing fields
// would otherwise produce something unreadable.
const MAX_MESSAGE_LENGTH = 200;

// Keys the API uses for errors that aren't tied to one specific form field.
const GENERIC_ERROR_KEYS = ["detail", "message", "error", "non_field_errors"];

// Words kept lowercase so a prefix reads "Date of Return", not "Date Of Return".
const MINOR_WORDS = ["of", "the", "a", "an", "to", "for", "in", "on", "at", "by"];

const humanizeField = function (field) {
  return String(field)
    .replace(/_/g, " ")
    .split(" ")
    .map(function (word, index) {
      if (!word) return word;
      if (index > 0 && MINOR_WORDS.indexOf(word.toLowerCase()) > -1) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const truncate = function (text) {
  if (text.length <= MAX_MESSAGE_LENGTH) return text;
  return text.slice(0, MAX_MESSAGE_LENGTH - 1).replace(/\s+$/, "") + "…";
};

let errorFunctions = {
  checkResponseThenJson: function (response) {
    //Check status of error - if ok convert to json
    if (!response.ok) {
      return response;
    }
    return response.json();
  },
  checkResponseSent: function (response) {
    //Check status of error - this one is just status returns
    return response.ok;
  },

  /*
  Turns an error array into a single string. Nested serialisers put objects in
  here, so recurse into them - a plain join() would give "[object Object]".
   */
  flattenMessages: function (values) {
    return values
      .reduce(function (acc, item) {
        if (typeof item === "string") {
          if (item) acc.push(item);
        } else if (item && typeof item === "object") {
          const nested = errorFunctions.parseApiErrors(item);
          Object.keys(nested).forEach(function (key) {
            acc.push(nested[key]);
          });
        }
        return acc;
      }, [])
      .join(" ");
  },

  /*
  Flattens an API error body into a flat { fieldName: "message" } map, covering
  every shape the backend returns: { field: ["msg"] }, { detail: "msg" },
  { non_field_errors: ["msg"] }, ["msg"], "msg", and nested serialisers such as
  { media: [{ file: ["msg"] }] }. Never throws - an unrecognised shape yields {}
  so callers fall back to their own default message.
   */
  parseApiErrors: function (payload) {
    if (!payload) return {};
    if (typeof payload === "string") return { non_field_errors: payload };
    if (Array.isArray(payload)) {
      const joined = errorFunctions.flattenMessages(payload);
      return joined ? { non_field_errors: joined } : {};
    }
    if (typeof payload !== "object") return {};

    return Object.keys(payload).reduce(function (acc, field) {
      const value = payload[field];
      if (typeof value === "string") {
        if (value) acc[field] = value;
      } else if (Array.isArray(value)) {
        const joined = errorFunctions.flattenMessages(value);
        if (joined) acc[field] = joined;
      } else if (value && typeof value === "object") {
        Object.assign(acc, errorFunctions.parseApiErrors(value));
      }
      return acc;
    }, {});
  },

  /*
  Picks the one line best suited to a toast. Field-specific messages usually
  name their own field already ("Date of Return cannot be earlier than..."), so
  the humanised field name is only prefixed when it isn't mentioned yet.
   */
  toErrorMessage: function (payload, fallback) {
    const defaultMessage =
      fallback || "Something went wrong. Please try again.";

    let errors;
    try {
      errors = errorFunctions.parseApiErrors(payload);
    } catch (e) {
      return defaultMessage;
    }

    const fields = Object.keys(errors);
    if (!fields.length) return defaultMessage;

    const key =
      GENERIC_ERROR_KEYS.filter(function (k) {
        return errors[k];
      })[0] || fields[0];
    const message = errors[key];

    if (GENERIC_ERROR_KEYS.indexOf(key) > -1) return truncate(message);

    const label = humanizeField(key);
    const alreadyNamed =
      message.toLowerCase().indexOf(label.toLowerCase()) > -1;
    return truncate(alreadyNamed ? message : label + ": " + message);
  },
};

export default errorFunctions;
